import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { ActionButton } from '../../shared/ui/ActionBar';

export const VacanciesPage = () => {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({ categoryId: null });

  // Единая функция для получения данных с фильтрацией
  const getData = async (params = {}) => {
    try {
      // Параметры по умолчанию
      const defaultParams = {
        count: 5,
        offset: 0,
      };
      
      // Объединяем с переданными параметрами
      const requestParams = { ...defaultParams, ...params };
      
      console.log('Запрос вакансий с параметрами:', requestParams);
      
      // Используем единый эндпоинт
      const response = await apiRequests.hr.get(
        requestParams.count,
        requestParams.offset,
        requestParams.categoryId,
        requestParams.query
      );
      
      setData(response);
    } catch (error) {
      console.error('Ошибка при получении вакансий:', error);
      setData({ items: [], totalCount: 0 });
    }
  };

  const getCategories = () => {
    apiRequests.hr.getCategories(100).then((res) => setCategories(res.items));
  };
  
  const handleSearch = () => {
    if (searchValue.trim()) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('search', searchValue.trim());
        return newParams;
      });
      getData({ query: searchValue.trim() });
    } else {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('search');
        return newParams;
      });
      getData();
    }
  };

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setSearchParams((prev) => {
      if (filterValues.categoryId) {
        prev.set('categoryId', filterValues.categoryId);
      } else {
        prev.delete('categoryId');
      }
      return prev;
    });
    if (filterValues.categoryId) {
      getData({ categoryId: filterValues.categoryId });
    } else {
      getData();
    }
  };

  const resetFilters = () => {
    setFilterValues({ categoryId: null });
    setSearchParams({});
    getData();
  };

  useEffect(() => {
    getCategories();
    const categoryId = searchParams.get('categoryId');
    if (categoryId) {
      setFilterValues({ categoryId });
    }
  }, []);

  const handleDelete = async (id) => {
    await apiRequests.hr.deleteVacancy(id).then(() =>
      setData((prev) => ({
        totalCount: prev.totalCount,
        items: prev.items.filter((i) => i.id !== id),
      })),
    );
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories?.map((c) => ({ value: c.id, label: c.title })) || [],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск вакансий..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink="/HR/vacancies/create"
      />

      <div className="mb-3">
        {data.items?.length === 0 ? (
          <p className="text-center">По вашему запросу ничего не найдено</p>
        ) : (
          data.items?.map((item) => (
            <EntityCard
              key={item.id}
              title={item.title}
              fields={[
                { label: 'Ставка от', value: `${item.salaryFrom}${item.currency?.symbol || ''}` },
                { label: 'Ставка до', value: `${item.salaryTo}${item.currency?.symbol || ''}` },
                { label: 'Создано', value: dateToString(item.createdAt) },
              ]}
              editLink={`/HR/vacancies/${item.id}`}
              onDelete={() => handleDelete(item.id)}
              actions={
                <ActionButton
                  icon={Eye}
                  to={`/HR/vacancies/${item.id}/summaries`}
                  title="Просмотр заявок"
                  variant="primary"
                />
              }
            />
          ))
        )}
      </div>

      <Pagination
        count={data.totalCount}
        itemsPerPage={5}
        onChange={(page) => {
          // Передаем параметры фильтрации вместе с параметрами пагинации
          const params = {
            count: 5,
            offset: page * 5
          };
          
          // Добавляем параметры фильтрации из URL, если они есть
          const categoryId = searchParams.get('categoryId');
          const search = searchParams.get('search');
          
          if (categoryId) params.categoryId = categoryId;
          if (search) params.query = search;
          
          getData(params);
        }}
      />
    </>
  );
};
